import {
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Tooltip,
} from "@mui/material";
import Box from "@mui/material/Box";
import {
  all,
  always,
  complement,
  cond,
  filter,
  isNotNil,
  map,
  pipe,
  prop,
  props,
  propSatisfies,
  range,
  T,
} from "ramda";
import { useContext, useEffect, useRef } from "react";
import { defineMessages } from "react-intl";
import { useMsg } from "../../components/Msg/Msg";
import { LinearProgressWithLabel } from "../../components/Progress/LinearProgressWithLabel";
import { P } from "../../components/Typography";
import { gray500, gray900 } from "../../theme";
import { I18nContext } from "../I18n/I18nProvider";
import { Loaders } from "../QM/QueryRenderer";
import { AwardBadge } from "./AwardBadge";
import { ReactComponent as BulletCompleted } from "./bullet-point-completed.svg";
import { ReactComponent as BulletUncompleted } from "./bullet-point-uncompleted.svg";
import { useGamification } from "./GamificationProvider";
import { useAuth } from "../Authorization";

export const GAMIFICATION_STATUS = {
  EMPTY: "EMPTY",
  PROGRESS: "PROGRESS",
  SUCCESS: "SUCCESS",
};

const getMonthName = (date, formatLocal) => {
  return formatLocal(date, "LLLL");
};

const gamificationMessages = defineMessages({
  "gamification.popover.empty.title": {
    id: "gamification.popover.empty.title",
    defaultMessage: "Make progress on your goals",
  },
  "gamification.popover.empty.desc": {
    id: "gamification.popover.empty.desc",
    defaultMessage:
      "Complete the 3 activities below to unlock your {month} badge.",
  },
  "gamification.popover.progress.title": {
    id: "gamification.popover.progress.title",
    defaultMessage: "You're on your way!",
  },
  "gamification.popover.progress.desc": {
    id: "gamification.popover.progress.desc",
    defaultMessage:
      "Complete the 3 activities below to unlock your {month} badge.",
  },
  "gamification.popover.success.title": {
    id: "gamification.popover.success.title",
    defaultMessage: "Badge Unlocked!",
  },
  "gamification.popover.todos.session": {
    id: "gamification.popover.todos.session",
    defaultMessage: "Complete 1 session",
  },
  "gamification.popover.todos.goal": {
    id: "gamification.popover.todos.goal",
    defaultMessage: "Achieve a short-term goal",
  },
  "gamification.popover.todos.video": {
    id: "gamification.popover.todos.video",
    defaultMessage: "Watch a video",
  },
  "gamification.popover.success.content": {
    id: "gamification.popover.success.content",
    defaultMessage:
      "Great job! You’ve successfully completed this challenge and {month} badge is now saved in your collection.",
  },
  "gamification.popover.success.extra": {
    id: "gamification.popover.success.extra",
    defaultMessage:
      "Keep up the great work and continue your journey this month!",
  },
  "gamification.popover.awards.title": {
    id: "gamification.popover.awards.title",
    defaultMessage: "Awards",
  },
});

const JAKUB_TODO_YEAR = new Date().getFullYear(); // TODO: get year from somewhere, @jakub is not providing it
const MONTH_INDEX_TO_YEAR = [
  "JANUARY",
  "FEBRUARY",
  "MARCH",
  "APRIL",
  "MAY",
  "JUNE",
  "JULY",
  "AUGUST",
  "SEPTEMBER",
  "OCTOBER",
  "NOVEMBER",
  "DECEMBER",
];

const SCROLLBAR_GUTTER = 2;

export const getGamificationDataStatus = pipe(
  props(["completedSession", "completedShortTermGoal", "watchedVideo"]),
  cond([
    [all(complement(Boolean)), always(GAMIFICATION_STATUS.EMPTY)],
    [all(Boolean), always(GAMIFICATION_STATUS.SUCCESS)],
    [T, always(GAMIFICATION_STATUS.PROGRESS)],
  ]),
);

export const GamificationPopover = () => {
  // const { userQuery } = useAuth();
  // const assessed = !!userQuery.data.strengths?.length;
  // const evaluated = !!userQuery.data.values?.length;
  // const cannotSeeVideos = !assessed || !evaluated; // TODO?

  const { userBadgesQuery: query, currentMonthIndex } = useGamification();
  const msg = useMsg({ dict: gamificationMessages });
  const { i18n } = useContext(I18nContext);
  const queryData = query.data;
  const unlockTodos = [
    {
      text: msg("gamification.popover.todos.session"),
      completed: queryData?.completedSession,
    },
    {
      text: msg("gamification.popover.todos.goal"),
      completed: queryData?.completedShortTermGoal,
    },
    {
      text: msg("gamification.popover.todos.video"),
      completed: queryData?.watchedVideo,
      // secondary: cannotSeeVideos ? "Test of secondary message" : undefined,
    },
  ];
  const awards = pipe(
    always(range(0, 12)),
    map((index) => {
      const monthIndex = (currentMonthIndex + 1 + index) % 12;
      const isThisYear = monthIndex <= currentMonthIndex;
      const currentYear = new Date().getFullYear();

      return {
        index,
        monthIndex,
        year: isThisYear ? currentYear : currentYear - 1,
        completed: prop(MONTH_INDEX_TO_YEAR[monthIndex], queryData?.badges),
      };
    }),
    filter(propSatisfies(isNotNil, "completed")),
  )();
  // const _awards = pipe( propOr({}, "badges"), toPairs, map(([monthName, completed]) => ({ monthIndex: new Date(`${monthName} 1`).getMonth(), year: JAKUB_TODO_YEAR, completed, })) )(queryData);
  console.log({ awards, query });
  // TODO: fireworks(?) animation when badge is unlocked, animation when progress is made
  const status = getGamificationDataStatus(queryData);
  const thisMonth = getMonthName(new Date(), i18n.formatLocal);
  const { title, desc } = {
    [GAMIFICATION_STATUS.EMPTY]: {
      title: msg("gamification.popover.empty.title"),
      desc: msg("gamification.popover.empty.desc", {
        month: thisMonth,
      }),
    },
    [GAMIFICATION_STATUS.PROGRESS]: {
      title: msg("gamification.popover.progress.title"),
      desc: msg("gamification.popover.progress.desc", {
        month: thisMonth,
      }),
    },
    [GAMIFICATION_STATUS.SUCCESS]: {
      title: msg("gamification.popover.success.title"),
    },
  }[status];
  const progressPercent = Math.round(
    100 * (filter(prop("completed"), unlockTodos).length / unlockTodos.length),
  );

  const awardsContainerRef = useRef(null);
  useEffect(() => {
    if (awardsContainerRef.current) {
      const timeout = setTimeout(() => {
        if (awardsContainerRef.current) {
          awardsContainerRef.current.scrollLeft =
            awardsContainerRef.current.scrollWidth;
        }
      }, 0); // Defer until after DOM update
      return () => clearTimeout(timeout);
    }
  }, [query.isLoading]);

  if (query.isLoading)
    return (
      <Stack spacing={2} sx={{ p: 3, width: 500 }}>
        <Loaders.CircularBlock />
      </Stack>
    );

  return (
    <Stack
      spacing={2}
      useFlexGap
      divider={<Divider orientation="horizontal" flexItem />}
      sx={{ p: 3, width: 500 }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        <P emphasized>{title}</P>
        {desc && <P>{desc}</P>}
        <LinearProgressWithLabel value={progressPercent} />
      </Box>

      {status === GAMIFICATION_STATUS.SUCCESS ? (
        <Box sx={{ textAlign: "center", py: 2 }}>
          <Box
            sx={{
              width: "100%",
              mb: 2,
              display: "flex",
              justifyContent: "center",
            }}
          >
            <AwardBadge
              monthIndex={currentMonthIndex}
              year={JAKUB_TODO_YEAR}
              completed
              width={84}
            />
          </Box>
          <P sx={{ fontSize: 16, color: gray900, mb: 0.5 }}>
            {msg("gamification.popover.success.content", {
              month: thisMonth,
            })}
          </P>
          <P sx={{ fontSize: 14, color: gray500 }}>
            {msg("gamification.popover.success.extra", {
              month: thisMonth,
            })}
          </P>
        </Box>
      ) : (
        <List sx={{ p: 0 }}>
          {unlockTodos.map(({ text, completed, secondary }, index) => (
            <ListItem key={text} sx={{ pl: 0 }}>
              <ListItemIcon sx={{ minWidth: 30 }}>
                {completed ? <BulletCompleted /> : <BulletUncompleted />}
              </ListItemIcon>
              {/* <Tooltip
                key={text}
                title="Hello"
                followCursor
                // placement="top-start"
              > */}
              <ListItemText primary={text} secondary={secondary} />
              {/* </Tooltip> */}
            </ListItem>
          ))}
        </List>
      )}
      {!!awards.length && (
        <Stack direction={"column"} spacing={2} sx={{ mb: -SCROLLBAR_GUTTER }}>
          <P emphasized sx={{ fontWeight: 600 }}>
            {msg("gamification.popover.awards.title")}
          </P>
          <Stack
            direction="row"
            spacing={2}
            sx={{
              overflowX: "auto",
              pb: SCROLLBAR_GUTTER,
              // scrollbarGutter: "auto",
              // scrollbarWidth: 0,
            }}
            ref={awardsContainerRef}
          >
            {awards.map(({ monthIndex, year, completed }) => {
              return (
                <Box
                  key={`${monthIndex}_${year}`}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    width: 70,
                    // minWidth: 62,
                  }}
                >
                  <AwardBadge
                    monthIndex={monthIndex}
                    year={year}
                    completed={completed}
                    width={56}
                  />
                  <P sx={{ fontWeight: 400, fontSize: 14, color: "black" }}>
                    {getMonthName(new Date(year, monthIndex), i18n.formatLocal)}
                  </P>
                  <P sx={{ fontWeight: 400, fontSize: 10, color: gray500 }}>
                    {year}
                  </P>
                </Box>
              );
            })}
          </Stack>
        </Stack>
      )}
    </Stack>
  );
};
